/**
 * Centralized in-app route paths.
 *
 * Conventions:
 *   - Static routes are plain string constants.
 *   - Parameterized routes ship in two shapes:
 *       1. `pattern`  — the React Router path pattern (`/properties/:id`)
 *       2. `to(id)`   — a builder for `<Link to={...}>` and `navigate(...)`
 *     This lets us define routes in one place without sprinkling string
 *     concatenation across components.
 *   - Never hard-code routes elsewhere; always import from here.
 */

export const APP_ROUTES = {
    ROOT:             '/',
    SIGN_IN:          '/sign-in',
    FORGOT_PASSWORD:  '/forgot-password',
    RESET_PASSWORD:   '/reset-password',
    HOME:             '/dashboard',
    NOT_FOUND:        '*',
} as const;

export const ADMIN_ROUTES = {
    INSIGHT:         '/admin/insight',
    CLIENT_ACCOUNTS: '/admin/client-accounts',
    CLIENT_ACCOUNT_DETAIL: {
        pattern: '/admin/client-accounts/:id',
        to: (id: string) => `/admin/client-accounts/${encodeURIComponent(id)}`,
    },
    DATABASE:        '/admin/database',
    ANALYTICS:       '/admin/analytics',
    PLANS:           '/admin/plans',
    TICKETS:         '/admin/tickets',
    NOTIFICATIONS:   '/admin/notifications',
    HELP_CONTROLS:   '/admin/help-controls',
    PROFILE:         '/admin/profile',
    SECURITY:        '/admin/security',
    DATABASE_CLIENT: {
        pattern: '/admin/database/:id',
        to: (id: string) => `/admin/database/${encodeURIComponent(id)}`,
    },
} as const;

export const AUTH_ROUTES = {
    SIGN_IN:          APP_ROUTES.SIGN_IN,
    FORGOT_PASSWORD:  APP_ROUTES.FORGOT_PASSWORD,
    RESET_PASSWORD:   APP_ROUTES.RESET_PASSWORD,
} as const;

export const SIGNUP_ROUTES = {
    root:      '/signup',
    account:   '/signup/account',
    subtype:   '/signup/subtype',
    workMode:  '/signup/work-mode',
    plan:      '/signup/plan',
    details:   '/signup/details',
    verify:    '/signup/verify',
    onboard:   '/signup/onboard',
} as const;

export const ONBOARDING_ROUTES = {
    root: '/onboarding',
} as const;

export const PLANS_ROUTES = {
    root: '/plans',
} as const;

export const DASHBOARD_ROUTES = {
    HOME:          APP_ROUTES.HOME,
    ANALYTICS:     '/analytics',
    WEB_ANALYTICS: '/web-analytics',
    SETTINGS:      '/settings',
} as const;

export const SUPPORT_ROUTES = {
    list: '/support',
} as const;

export const NOTIFICATION_ROUTES = {
    list: '/notifications',
} as const;

export const SETTINGS_ROUTES = {
    root:            '/settings',
    profile:         '/settings/profile',
    twoFactor:       '/settings/2fa',
    branding:        '/settings/branding',
    integrations:    '/settings/integrations',
    sendingIdentity: '/settings/sending-identity',
    leadRouting:     '/settings/lead-routing',
    welcome:         '/settings/welcome',
    notifications:   '/settings/notifications',
    referrals:       '/settings/referrals',
    terms:           '/settings/terms',
} as const;

export const PROPERTY_ROUTES = {
    list:    '/properties',
    create:  '/properties/create',
} as const;

/** Inventory → Projects. `create` on PROPERTY_ROUTES takes `?project=<id>` to
 *  open the new-property form already filed under a project. */
export const PROJECT_ROUTES = {
    list:   '/projects',
    detail: {
        pattern: '/projects/:id',
        to: (id: string) => `/projects/${encodeURIComponent(id)}`,
    },
} as const;

export const LEAD_ROUTES = {
    list:    '/leads',
    detail: {
        pattern: '/leads/:id',
        to: (id: string) => `/leads/${encodeURIComponent(id)}`,
    },
} as const;

export const QR_ROUTES = {
    list: '/qr-codes',
    new:  '/qr-codes/new',
} as const;

export const BUILDER_ROUTES = {
    list: '/builders',
} as const;

export const TOUR_ROUTES = {
    list: '/tours',
} as const;

export const SALES_TEAM_ROUTES = {
    list: '/sales-team',
} as const;

export const ROLE_MATRIX_ROUTES = {
    root: '/role-matrix',
} as const;

export const COMPARE_ROUTES = {
    root: '/compare',
} as const;

export const BILLING_ROUTES = {
    root: '/billing',
    /** Opens Billing with the plan picker — used after the upgrade page CTA. */
    upgrade: (feature?: string) => {
        const params = new URLSearchParams({ upgrade: '1' });
        if (feature) params.set('feature', feature);
        return `/billing?${params.toString()}`;
    },
} as const;

export const UPGRADE_ROUTES = {
    root: '/upgrade',
    /** Plan/permission gate destination — mirrors the Upgrade popup context. */
    withContext: (feature?: string, reason?: 'plan' | 'permission') => {
        const params = new URLSearchParams();
        if (feature) params.set('feature', feature);
        if (reason) params.set('reason', reason);
        const query = params.toString();
        return query ? `/upgrade?${query}` : UPGRADE_ROUTES.root;
    },
} as const;
